import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Mail, Calendar, Heart, Trash2 } from 'lucide-react';
import api from '../api';
import { format } from 'date-fns';

export default function UserDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        loadUser();
    }, [id]);

    const loadUser = async () => {
        try {
            const data = await api.getUsers();
            const found = data.find(u => u.id === id);
            setUser(found);
        } catch (error) {
            console.error('Error loading user:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to delete this user?')) return;

        try {
            await api.deleteUser(id);
            navigate('/admin', { state: { activeTab: 'users' } });
        } catch (error) {
            alert('Error deleting user: ' + error.message);
        }
    };

    if (loading) {
        return (
            <main className="main">
                <div className="skeleton" style={{ height: '100vh' }} />
            </main>
        );
    }

    if (!user) {
        return (
            <main className="main">
                <div className="container" style={{ padding: '80px 24px', textAlign: 'center' }}>
                    <h1>User not found</h1>
                    <button onClick={() => navigate('/admin', { state: { activeTab: 'users' } })} className="btn btn-primary" style={{ marginTop: '20px' }}>
                        Back to Users
                    </button>
                </div>
            </main>
        );
    }

    return (
        <main className="main" style={{ background: 'var(--bg-admin)', minHeight: '100vh' }}>
            <div className="container" style={{ maxWidth: '900px', padding: '40px 24px' }}>
                {/* Back Button */}
                <button
                    onClick={() => navigate('/admin', { state: { activeTab: 'users' } })}
                    className="btn btn-ghost"
                    style={{ marginBottom: '24px', padding: '8px 16px' }}
                >
                    <ArrowLeft size={18} /> Back to Users
                </button>

                {/* User Profile Card */}
                <div className="card" style={{ padding: '40px', marginBottom: '24px' }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        marginBottom: '32px',
                        paddingBottom: '24px',
                        borderBottom: '1px solid var(--border)'
                    }}>
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '24px' }}>
                            <div style={{
                                width: '100px',
                                height: '100px',
                                borderRadius: '50%',
                                background: 'var(--primary-gradient)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#000',
                                fontSize: '40px',
                                fontWeight: '700',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                            }}>
                                {user.name?.charAt(0) || user.email?.charAt(0) || 'U'}
                            </div>
                            <div>
                                <h1 style={{
                                    fontSize: '32px',
                                    fontWeight: '700',
                                    color: 'var(--text-primary)',
                                    marginBottom: '8px'
                                }}>
                                    {user.name || 'User'}
                                </h1>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-secondary)' }}>
                                    <Mail size={16} />
                                    {user.email}
                                </div>
                                <span style={{
                                    display: 'inline-block',
                                    background: user.role === 'admin' ? 'var(--primary-gradient)' : 'var(--bg-secondary)',
                                    padding: '6px 16px',
                                    borderRadius: '16px',
                                    fontSize: '13px',
                                    fontWeight: '500',
                                    marginTop: '12px',
                                    color: user.role === 'admin' ? '#000' : 'var(--text-secondary)'
                                }}>
                                    {user.role || 'User'}
                                </span>
                            </div>
                        </div>

                        <button
                            onClick={handleDelete}
                            className="btn btn-secondary"
                            style={{ color: 'var(--error)', display: 'flex', alignItems: 'center', gap: '8px' }}
                        >
                            <Trash2 size={18} />
                            Delete
                        </button>
                    </div>

                    {/* Account Information */}
                    <div style={{ marginBottom: '32px' }}>
                        <h3 style={{
                            fontSize: '18px',
                            fontWeight: '600',
                            marginBottom: '16px',
                            color: 'var(--text-primary)'
                        }}>
                            Account Information
                        </h3>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                            gap: '16px'
                        }}>
                            <div style={{
                                background: 'var(--bg-secondary)',
                                padding: '16px',
                                borderRadius: '12px'
                            }}>
                                <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '4px' }}>
                                    Member Since
                                </div>
                                <div style={{ fontSize: '15px', fontWeight: '500' }}>
                                    <Calendar size={14} style={{ display: 'inline', marginRight: '6px' }} />
                                    {user.created_at ? format(new Date(user.created_at), 'MMM d, yyyy') : 'N/A'}
                                </div>
                            </div>
                            <div style={{
                                background: 'var(--bg-secondary)',
                                padding: '16px',
                                borderRadius: '12px'
                            }}>
                                <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '4px' }}>
                                    Favorite Events
                                </div>
                                <div style={{ fontSize: '15px', fontWeight: '500' }}>
                                    <Heart size={14} style={{ display: 'inline', marginRight: '6px' }} />
                                    {user.favorites?.length || 0} events
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
}

